import * as React from 'react';

import classNames from 'classnames';

import {FormField} from 'shells/form/form-field';
import {CalendarSquareRegularAgendaIcon} from 'shells/icon/svg/calendar-square-regular-agenda-icon';
import {IconSection} from 'shells/icon-section/icon-section';
import {RichTextDisplay} from 'shells/rich-text-display/rich-text-display';
import {TextField} from 'shells/textfield/textfield';

import styles from './calendar.module.css';

type Props = {
    agenda: string;
    linkifiedAgenda?: string;
    onChange: (newAgenda: string) => void;
    isReadOnly?: boolean;
    // We do not need to show rich text on the schedule page
    forceTextfield?: boolean;
    autoFocus?: boolean;
};

export function AgendaSection(props: Props) {
    const {isReadOnly = false, forceTextfield = false} = props;

    const [shouldShowLinks, setShouldShowLinks] = React.useState<boolean>(true);
    const textFieldRef = React.useRef<HTMLInputElement | null | undefined>(null);

    React.useEffect(() => {
        setShouldShowLinks(true);
    }, [props.linkifiedAgenda]);

    const [isEditing, setIsEditing] = React.useState<boolean>(false);

    const handleAgendaChange = (newAgenda: string) => {
        if (newAgenda === props.agenda) {
            return;
        }

        setShouldShowLinks(false);
        props.onChange(newAgenda);
    };

    React.useEffect(() => {
        if (textFieldRef.current) {
            const textField = textFieldRef.current;
            const length = textField.value.length;
            textField.focus();
            textField.setSelectionRange(length, length);
        }
    }, [isEditing]);

    const richTextDisplayStyleName = classNames(styles['agenda-display'], {
        [styles.disabled]: isReadOnly,
    });

    return (
        <IconSection icon={CalendarSquareRegularAgendaIcon} fullWidth={true}>
            <FormField innerId='agenda' noMargin={true} isFullWidth={true}>
                <div className={styles['agenda-container']}>
                    {(isEditing && !isReadOnly) || forceTextfield ? (
                        <TextField
                            // @ts-expect-error
                            name='agenda'
                            placeholder='Add an agenda to share with your attendees'
                            value={props.agenda}
                            onChange={handleAgendaChange}
                            multiline={true}
                            autoFocus={props.autoFocus}
                            onBlur={() => setIsEditing(false)}
                            shouldGrowVertically={true}
                            minHeight={150}
                            textFieldRef={textFieldRef}
                            staticLineHeight={true}
                        />
                    ) : (
                        <div
                            onClick={() => setIsEditing(true)}
                            className={richTextDisplayStyleName}
                        >
                            <RichTextDisplay
                                html={
                                    shouldShowLinks && props.linkifiedAgenda
                                        ? props.linkifiedAgenda
                                        : props.agenda
                                }
                                size='small'
                                smallLineHeight={true}
                                placeholderText='Add an agenda to share with your attendees'
                                maintainLineBreaks={true}
                                color={isReadOnly ? 'light' : 'medium'}
                            />
                        </div>
                    )}
                </div>
            </FormField>
        </IconSection>
    );
}
